import { CATEGORY_MAP } from '@ridehub/shared';
import type { AiSpot } from './types.js';

interface Props {
  spot: AiSpot;
  userLat?: number;
  userLng?: number;
}

function calcDistanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

export function AiSpotCard({ spot, userLat, userLng }: Props) {
  const categoryDef = CATEGORY_MAP.get(spot.category);
  // 名称＋住所で検索（POI検索と住所絞り込みを両立）、住所なしは座標にフォールバック
  const navDestination = spot.address
    ? `${spot.name} ${spot.address}`
    : spot.lat != null && spot.lng != null
      ? `${spot.lat},${spot.lng}`
      : null;
  const navUrl = navDestination
    ? `https://maps.google.com/maps/dir/?api=1&destination=${encodeURIComponent(navDestination)}&travelmode=bicycling`
    : null;

  const distance =
    userLat != null && userLng != null && spot.lat != null && spot.lng != null
      ? calcDistanceMeters(userLat, userLng, spot.lat, spot.lng)
      : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col gap-2">
      {/* ヘッダー行 */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{spot.name}</h3>
          {spot.address && (
            <p className="text-xs text-gray-500 mt-0.5 leading-tight">{spot.address}</p>
          )}
        </div>
        {/* カテゴリバッジ */}
        {categoryDef && (
          <span
            className="shrink-0 text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ backgroundColor: `${categoryDef.color}40`, color: '#444' }}
          >
            {categoryDef.icon} {categoryDef.label}
          </span>
        )}
      </div>

      {/* 説明文 */}
      {spot.description && (
        <p className="text-xs text-gray-600 leading-relaxed">{spot.description}</p>
      )}

      {/* フッター行 */}
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-2">
          {/* AIスポット未検証バッジ */}
          <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-medium">
            AIスポット（未検証）
          </span>
          {/* 現在地からの距離 */}
          {distance != null && (
            <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path
                  d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
              {formatDistance(distance)}
            </span>
          )}
        </div>

        {/* Google Maps ナビリンク */}
        {navUrl && (
          <a
            href={navUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-primary font-medium active:opacity-70"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 11l19-9-9 19-2-8-8-2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            ナビ起動
          </a>
        )}
      </div>
    </div>
  );
}
